import { History, Loader2, RotateCcw } from "lucide-react";
import { marked } from "marked";
import type React from "react";
import { useMemo } from "react";
import { useAuth } from "@/features/auth";
import { ChatState } from "@/features/project";
import { useTab } from "@/features/Tab";
import { useUI } from "@/features/ui";
import { MessageApprovalPanel } from "./MessageApprovalPanel";
import type { ChatMessageProps } from "./types";

marked.setOptions({
  breaks: true,
  gfm: true,
});

const MessageContent = ({ content }: { content: string }) => (
  <div
    className="prose prose-sm prose-invert max-w-none prose-p:my-2 prose-headings:my-3 prose-ul:my-2 prose-ol:my-2 prose-li:my-0 prose-pre:bg-slate-800 prose-pre:text-slate-200 prose-code:text-emerald-400 prose-code:before:content-none prose-code:after:content-none"
    dangerouslySetInnerHTML={{ __html: content }}
  />
);

export const AiMessage: React.FC<ChatMessageProps> = ({
  message,
  isLast,
  onRestore,
  onRetry,
  streamingPhase,
}) => {
  const { user } = useAuth();
  const { triggerExportHighlight } = useUI();
  const { setViewMode } = useTab();

  const renderedContent = useMemo(() => {
    if (!message.content) return "";
    return marked.parse(message.content) as string;
  }, [message.content]);

  return (
    <div
      className="w-full mb-4 text-sm leading-relaxed text-slate-200"
      onClick={(e) => {
        const target = (e.target as HTMLElement).closest("a");
        if (target) {
          const href = target.getAttribute("href");
          if (href === "action:highlight-export") {
            e.preventDefault();
            setViewMode("preview");
            triggerExportHighlight();
          }
        }
      }}
    >
      {message.content && <MessageContent content={renderedContent} />}

      {/* Streaming Indicator */}
      {streamingPhase === ChatState.THINKING && (
        <div className="flex items-center gap-2 text-xs font-mono text-slate-400 mt-1">
          <Loader2 size={12} className="animate-spin" />
          <span className="animate-pulse">Thinking...</span>
        </div>
      )}
      {streamingPhase === ChatState.CODING && (
        <div
          className={`flex items-center gap-2 text-xs font-mono mt-1 ${
            message.content ? "pt-2 border-t border-slate-700/50" : ""
          } text-emerald-400`}
        >
          <Loader2 size={12} className="animate-spin" />
          <span className="animate-pulse">Writing code...</span>
        </div>
      )}
      {message.error && <div className="mt-2 text-xs text-red-400 font-mono">{message.error}</div>}

      <MessageApprovalPanel message={message} isLast={isLast} />

      <div className="flex items-center gap-3 mt-2">
        {message.timestamp > 0 && (
          <span className="text-[10px] text-slate-600">
            {new Date(message.timestamp).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        )}
        {onRetry && message.error && (
          <button
            onClick={onRetry}
            className="flex items-center gap-1 text-[10px] transition-colors px-1.5 py-0.5 rounded text-orange-400 hover:text-orange-300 bg-slate-800/50 hover:bg-slate-700 border border-slate-700"
            title="Retry this message"
          >
            <RotateCcw size={10} />
            <span>Retry</span>
          </button>
        )}
        <div className="flex gap-1">
          {/* Non-error retry - subtle gray */}
          {onRetry && !message.error && (user.isAdmin || import.meta.env.DEV) && (
            <button
              onClick={() => {
                if (window.confirm("Retry will discard the current response. Continue?")) {
                  onRetry();
                }
              }}
              className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-300 transition-colors px-1.5 py-0.5 rounded hover:bg-slate-800"
              title="Retry this message"
            >
              <RotateCcw size={10} />
              <span>Retry</span>
            </button>
          )}
          {/* Restore - indigo color */}
          {onRestore && streamingPhase === ChatState.IDLE && (
            <button
              onClick={onRestore}
              className="flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-300 transition-colors px-1.5 py-0.5 rounded hover:bg-slate-800"
              title="Restore this version"
            >
              <History size={10} />
              <span>Restore</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
